"use client";

import { Button } from "@/components/ui/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/primitives/dialog";
import { cn } from "@/lib/utils/general-utils";
import { CircleCheck, Info, TriangleAlert } from "lucide-react";
import { useCallback } from "react";

export interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description: string;
  children?: React.ReactNode;
  variant?: "danger" | "warning" | "info" | "success";
  confirmText?: string;
  cancelText?: string;
  isConfirming?: boolean;
  size?: "sm" | "md" | "lg";
  actionsAlign?: "split" | "end";
}

const variantConfig = {
  danger: {
    icon: TriangleAlert,
    iconColor: "text-destructive",
    iconBg: "bg-destructive/10",
    buttonVariant: "destructive" as const,
  },
  warning: {
    icon: TriangleAlert,
    iconColor: "text-yellow-600",
    iconBg: "bg-yellow-100",
    buttonVariant: "default" as const,
  },
  info: {
    icon: Info,
    iconColor: "text-blue-600",
    iconBg: "bg-blue-100",
    buttonVariant: "default" as const,
  },
  success: {
    icon: CircleCheck,
    iconColor: "text-green-600",
    iconBg: "bg-green-100",
    buttonVariant: "default" as const,
  },
} as const;

export function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  children,
  variant = "danger",
  confirmText = "Confirm",
  cancelText = "Cancel",
  isConfirming = false,
  size = "md",
  actionsAlign = "end",
}: Readonly<ConfirmationDialogProps>) {
  const handleClose = useCallback(() => {
    if (!isConfirming) onClose();
  }, [onClose, isConfirming]);

  const handleConfirm = useCallback(async () => {
    await onConfirm();
  }, [onConfirm]);

  const config = variantConfig[variant];
  const Icon = config.icon;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent size={size} showCloseButton={!isConfirming}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="px-6 py-6">
          <div className="flex gap-4">
            <div
              className={cn(
                "flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center",
                config.iconBg,
              )}
            >
              <Icon className={cn("w-6 h-6", config.iconColor)} />
            </div>
            <div className="flex-1 pt-1">
              <p className="text-sm text-muted-foreground">{description}</p>
            </div>
          </div>
          {children && <div className="mt-4">{children}</div>}
        </div>

        <DialogFooter>
          <div
            className={cn(
              "flex flex-col-reverse sm:flex-row sm:items-center gap-3",
              actionsAlign === "end" ? "sm:justify-end" : "sm:justify-between",
            )}
          >
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isConfirming}
            >
              {cancelText}
            </Button>
            <Button
              type="button"
              variant={config.buttonVariant}
              onClick={handleConfirm}
              disabled={isConfirming}
              loading={isConfirming}
              loadingText="Processing..."
            >
              {confirmText}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
